// "O app": Ordexa installed on the phone's home screen (no app store). Chrome on Android offers the
// install prompt once, early (beforeinstallprompt), so it is kept here from the first page load for the
// screens that offer "Instalar o app". iPhone has no prompt: Compartilhar > Adicionar à Tela de Início.
import { track } from "@/utils/analytics";

let deferredPrompt = null;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (e) {
      // ignore
    }
  });
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    try {
      localStorage.setItem("ordexa/app-installed", new Date().toISOString());
    } catch (e) {
      // ignore
    }
    track("app_installed", { platform: platform() });
    notify();
  });
}

export function onInstallChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Opened from the home-screen icon.
export function isApp() {
  if (typeof window === "undefined") return false;
  return (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true;
}

// Browsers inside other apps (Gmail, Instagram, Facebook, LinkedIn…) cannot install: open in Safari/Chrome first.
export function isInAppBrowser() {
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Line\/|GSA\/|; wv\)|WhatsApp|TikTok|Snapchat/i.test(ua);
}

export function platform() {
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

// iPhone: Safari is the sure way; other iOS browsers also offer "Adicionar à Tela de Início" in their share menu.
export function iosBrowser() {
  const ua = navigator.userAgent || "";
  if (/CriOS/.test(ua)) return "chrome";
  if (/FxiOS/.test(ua)) return "firefox";
  if (/EdgiOS/.test(ua)) return "edge";
  return "safari";
}

export function canPrompt() {
  return !!deferredPrompt;
}

export function wasInstalled() {
  try {
    return isApp() || !!localStorage.getItem("ordexa/app-installed");
  } catch (e) {
    return isApp();
  }
}

// Shows Chrome's own install dialog. Resolves to "accepted", "dismissed" or "unavailable".
export async function promptInstall(where) {
  if (!deferredPrompt) return "unavailable";
  const prompt = deferredPrompt;
  deferredPrompt = null;
  prompt.prompt();
  let outcome = "dismissed";
  try {
    outcome = (await prompt.userChoice).outcome;
  } catch (e) {
    // ignore
  }
  track("app_install_answer", { outcome, where: where || "" });
  notify();
  return outcome;
}

// Android in-app browser: reopen this page in Chrome, where it can be installed.
export function chromeIntentUrl(path) {
  const url = new URL(path || window.location.pathname + window.location.search, window.location.origin);
  return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url.href)};end`;
}
